import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Stack, TextField, MenuItem, CircularProgress, Alert,
  TablePagination, Tooltip, IconButton, Collapse,
} from '@mui/material';
import { Search, ExpandMore, ExpandLess, Refresh } from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { httpClient } from '../services/http-client';

interface AuditEvent {
  id: string;
  actor_name?: string;
  action: string;
  entity_type?: string;
  entity_label?: string;
  outcome: 'ok' | 'warning' | 'error';
  summary: string;
  details?: Record<string, any>;
  created_at: string;
}

const OUTCOME_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  ok: 'success', warning: 'warning', error: 'error',
};

/**
 * What the household and the system have actually done.
 *
 * Answering "did that sync work, and what came back" used to mean reading
 * container logs over SSH — fine for an operator, useless for anyone else, and
 * the reason several real defects survived here for weeks. A connection
 * returning zero holdings looked identical to one with nothing to return, and
 * neither left a trace anyone would find.
 *
 * So this shows OUTCOMES, not intentions: row counts, what the provider
 * returned, what was skipped. "Sync ran" would have hidden every one of them.
 */
export const ActivityLogPage: React.FC = () => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [outcome, setOutcome] = useState('');
  const [actions, setActions] = useState<string[]>([]);
  const [sortDesc, setSortDesc] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (action) params.set('action', action);
      if (outcome) params.set('outcome', outcome);
      params.set('sort', sortDesc ? 'desc' : 'asc');
      params.set('limit', String(rowsPerPage));
      params.set('offset', String(page * rowsPerPage));
      const res = await httpClient.get<{ events: AuditEvent[]; total: number }>(
        `/audit?${params.toString()}`);
      setEvents(res.events ?? []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      setError(e?.message ?? 'Could not load activity');
    } finally {
      setLoading(false);
    }
  }, [search, action, outcome, sortDesc, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    httpClient.get<string[]>('/audit/actions')
      .then((a) => setActions(Array.isArray(a) ? a : []))
      .catch(() => setActions([]));
  }, []);

  return (
    <MainLayout>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Activity</Typography>
            <Typography variant="body2" color="text.secondary">
              Everything this household and FolioWorth have done — syncs, imports,
              reconnects — and what each one actually returned.
            </Typography>
          </Box>
          <Tooltip title="Reload">
            <IconButton onClick={load} aria-label="Reload activity"><Refresh /></IconButton>
          </Tooltip>
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                placeholder="Search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, opacity: 0.6 }} /> }}
                sx={{ minWidth: 220 }}
              />
              <TextField
                select size="small" label="Action" value={action}
                onChange={(e) => { setAction(e.target.value); setPage(0); }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All actions</MenuItem>
                {/* Grouped by prefix, so "connection" catches sync, reconnect
                    and disconnect together — which is how people think. */}
                {Array.from(new Set(actions.map((a) => a.split('.')[0]))).map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Outcome" value={outcome}
                onChange={(e) => { setOutcome(e.target.value); setPage(0); }}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="">Everything</MenuItem>
                {/* The query that matters most: what went wrong. */}
                <MenuItem value="problems">Problems only</MenuItem>
                <MenuItem value="ok">Successful</MenuItem>
                <MenuItem value="warning">Warnings</MenuItem>
                <MenuItem value="error">Errors</MenuItem>
              </TextField>
              <TextField
                select size="small" label="Order" value={sortDesc ? 'desc' : 'asc'}
                onChange={(e) => setSortDesc(e.target.value === 'desc')}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="desc">Newest first</MenuItem>
                <MenuItem value="asc">Oldest first</MenuItem>
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Card>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}

          {!loading && events.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Nothing recorded yet for this filter.
              </Typography>
            </Box>
          )}

          {!loading && events.length > 0 && (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>When</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>What happened</TableCell>
                    <TableCell>Who</TableCell>
                    <TableCell>Outcome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((e) => (
                    <React.Fragment key={e.id}>
                      <TableRow hover>
                        <TableCell>
                          {e.details && Object.keys(e.details).length > 0 && (
                            <IconButton
                              size="small"
                              aria-label={expanded === e.id ? 'Hide detail' : 'Show detail'}
                              onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                            >
                              {expanded === e.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(e.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={e.action} variant="outlined" sx={{ height: 20 }} />
                        </TableCell>
                        <TableCell>{e.summary}</TableCell>
                        <TableCell>
                          {/* Blank means FolioWorth acted on its own — a
                              scheduled job. Attributing that to a person would
                              make automated work look like somebody's choice. */}
                          {e.actor_name || <em style={{ opacity: 0.6 }}>system</em>}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={e.outcome}
                            color={OUTCOME_COLOR[e.outcome] ?? 'default'}
                            sx={{ height: 20 }}
                          />
                        </TableCell>
                      </TableRow>
                      {expanded === e.id && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ bgcolor: 'action.hover', py: 1 }}>
                            <Collapse in>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {Object.entries(e.details ?? {}).map(([k, v]) => (
                                  <Chip key={k} size="small" variant="outlined"
                                    label={`${k}: ${String(v)}`} />
                                ))}
                              </Stack>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </>
          )}
        </Card>
      </Box>
    </MainLayout>
  );
};
